import React, {useContext, useEffect, useState} from 'react';
import Typography from '@material-ui/core/Typography';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';

import {makeStyles} from "@material-ui/core/styles";

import { MilestoneTypes, MilestoneStatus} from "../../utils/constants";
import apiFetch from "../../utils/apiUtils";
import {ErrorContext} from "../../utils/constants";
import {formatDateNoTimezone} from "../../utils/dateUtils";
import {useParams} from "react-router-dom";
import Colors from '../../utils/colors';
import LxApprovalButtons from '../initiative/LxApprovalButtons';
import MilestoneEditor from './MilestoneEditor';
import ExpansionCard from '../ExpansionCard';
import {isOidLoggedInUser} from '../../utils/initiatives';
import '@fontsource/inter';

const textStyle = {
    fontFamily: 'Inter',
    fontStyle: 'normal',
    fontWeight: 500,
};

const isMandatoryMilestone = (type) => type !== 0 && type !== 5 && type !== 6;
const isMoneyStepMilestone = (type) => type === MilestoneTypes[5].label;
const isEmbedSubmissionMilestone = (type) => type === MilestoneTypes[6].label;

const formatDate = (date) => {
  return formatDateNoTimezone(date, 'MMMM D, YYYY');
};

const formatType = (type) => {
    return MilestoneTypes[type].label;
};

const useStyles = makeStyles(() => ({
    cardRoot: {
        width: '100%',
        display: 'block'
    },
    milestoneStatusBar: {
        columnCount: 3
    },
    milestoneCardHeader: {
        ...textStyle,
        fontSize: '14px',
        lineHeight: '16px',
        letterSpacing: '1.5px',
        textTransform: 'uppercase'
    },
    approvalButton: {
        margin: '1rem',
        textAlign: 'center'
    },
    approvalContainer: {
      display: 'flex',
      justifyContent: 'space-around'
    },
    mandatoryApproverStyle: {
      backgroundColor: Colors.darkBlue,
      width: '30%'
    },
    statusDiv: {
      display: 'flex',
      justifyContent: 'center',
      width: '100%'
    },
    checkedCircle: {
      marginRight: '1rem',
      color: Colors.tertiary
    }
}));

const DeliverHeader = ({ status, initiative, onSuccess }) => {
  const classes = useStyles();
  const blurb = initiative._links['DELIVER_SPONSOR_APPROVAL'] || initiative._links['DELIVER_TO_APPROVAL'] ?
    'After reviewing and analysing this initiative, can this initiative proceed to Deliver phase?' :
    'Now that you have marked your Delivery Step complete, indicating that this initiative has been delivered, submit your initiative for approval to Delivery.';
  const milestoneCompleted = (status === MilestoneStatus.find(m => m.label === 'Completed').value);
  const shouldShow = () => initiative.confirm_sponsor_user && (!initiative.requiresGreaterApproval || initiative.confirm_to_user) && milestoneCompleted;
  return !!shouldShow() ? (
    <div>
    <br />
      <Typography variant="h5" component="h2" className={classes.milestoneCardHeader}>
        Submit for Approval
      </Typography>
      <Typography variant="body1">
          {blurb}
      </Typography>
      <LxApprovalButtons
        initiative={initiative}
        onSuccess={onSuccess}
        level='DELIVER'
        buttonText='SUBMIT FOR APPROVAL'
      />
    </div>
  ) : (<div/>);
};

const EmbedHeader = ({ initiative, onSuccess }) => {
  const shouldShow = () => initiative.deliver_sponsor_user && (!initiative.requiresGreaterApproval || initiative.deliver_to_user);
  return shouldShow() ? (
    <div>
    <br />
      <Typography variant="body1">
          {(initiative._links['EMBED_SPONSOR_APPROVAL'] || initiative._links['EMBED_TO_APPROVAL']) && 'Embed - After reviewing and analysing this initiative, can this initiative proceed to Embed?'}
      </Typography>
      <LxApprovalButtons
        initiative={initiative}
        onSuccess={onSuccess}
        level='EMBED'
      />
    </div>
  ) : (<div/>);
}

const MilestoneCard = ({ type, owner, description, status, startDate, endDate, initiative, onSuccess, milestoneId, disabled }) => {
    const classes = useStyles();
    const params = useParams();
    const { triggerError } = useContext(ErrorContext);
    const [ statusId, setStatusId ] = useState(status);

    useEffect(() => {
      setStatusId(status);
    }, [status]);

    const updateStatus = (e) => {
      apiFetch('/initiatives/' + params.id + '/milestones/' + milestoneId + '/status',
        'PUT', JSON.stringify({ statusId: e.target.value }),
        () => { setStatusId(e.target.value); onSuccess() },
        () => triggerError({
          title: 'Update Status Error',
          description: `We were unable to update the status of this milestone. Please try again.`,
          onRetry: () => updateStatus(e)
      }));
    }

    return (
        <div className={classes.cardRoot}>
            <Typography variant="h5" component="h2" className={classes.milestoneCardHeader}>
                Milestone Type
            </Typography>
            <Typography variant="body1">
                {type}
            </Typography>
            <br />
            {isEmbedSubmissionMilestone(type) || <Typography variant="h5" component="h2" className={classes.milestoneCardHeader}>
                Milestone Owner
            </Typography>}
            {isEmbedSubmissionMilestone(type) || <Typography variant="body1">
                {owner}
            </Typography>}
            <br />
            <Typography variant="h5" component="h2" className={classes.milestoneCardHeader}>
                Description
            </Typography>
            <Typography>
                {description}
            </Typography>
            <br />
            {isEmbedSubmissionMilestone(type) || <div className={classes.milestoneStatusBar}>
                <Typography variant="h5" component="h2" className={classes.milestoneCardHeader}>
                    Start Date
                </Typography>
                {startDate}
                <Typography variant="h5" component="h2" className={classes.milestoneCardHeader}>
                    End Date
                </Typography>
                {endDate}
                <Typography variant="h5" component="h2" className={classes.milestoneCardHeader}>
                    Milestone Status
                </Typography>
                <Select
                  value={statusId}
                  onChange={updateStatus}
                  disabled={disabled}
                >
                  {MilestoneStatus.filter(status => status.label !== "Resubmitted").map(({value, label}, index) => (<MenuItem value={value} key={index}>{label}</MenuItem>))}
                </Select>
            </div>}
            {isMoneyStepMilestone(type) && <DeliverHeader
              status={status}
              initiative={initiative}
              onSuccess={onSuccess}
            />}
            {isEmbedSubmissionMilestone(type) && <EmbedHeader
              initiative={initiative}
              onSuccess={onSuccess}
            />}
        </div>
    );
};

export default function MilestoneList({milestones, initiative, onSuccess, disabled}) {
    const [ activeEditors, setActiveEditors ] = useState([]);

    const finishEdit = (id) => () => setActiveEditors(activeEditors.filter(item => item !== id))
    const enableEdit = (id) => () => setActiveEditors([...activeEditors, id]);
    const isInEditMode = (id) => activeEditors.indexOf(id) !== -1;

    return (
        <div>
            {milestones.filter(({type}) => !isMandatoryMilestone(type)).map((milestone) => (
                <ExpansionCard
                    header={milestone.name}
                    milestone={milestone}
                    isDelivery={isMoneyStepMilestone(formatType(milestone.type))}
                    key={`ms_${milestone.id}`}
                    subheader={milestone.endDate? `Due on ${formatDate(milestone.endDate)}` : null}
                    activeIcon={milestone.type !== 6 && (!disabled || isOidLoggedInUser(milestone.owner_oid))? <EditOutlinedIcon/> : null}
                    onClickActiveIcon={enableEdit(milestone.id)}
                    detailsCard={isInEditMode(milestone.id) ?
                        <MilestoneEditor
                            key={`edit-${milestone.id}`}
                            initiativeId={milestone.initiative_id}
                            milestone={milestone}
                            onClose={finishEdit(milestone.id)}
                            onSuccess={onSuccess}
                            disabled={disabled && !isOidLoggedInUser(milestone.owner_oid)}
                            typeDisabled={disabled || (milestone.confidential && isOidLoggedInUser(milestone.owner_oid))}
                            milestones={milestones}
                        /> : <MilestoneCard
                        type={formatType(milestone.type)}
                        owner={milestone.owner}
                        description={milestone.description}
                        status={milestone.status}
                        startDate={formatDate(milestone.startDate)}
                        endDate={formatDate(milestone.endDate)}
                        milestoneId={milestone.id}
                        initiative={initiative}
                        onSuccess={onSuccess}
                        disabled={disabled && !isOidLoggedInUser(milestone.owner_oid)}
                    />}
                />
            ))}
        </div>
    );
}
